import { isSourceImageId, } from './index.js';
export const FREDRICK_SEARCH_CLAIM_SCHEMA_VERSION = 1;
export const FREDRICK_SEARCH_CLAIM_ID_PREFIX = 'claim-v1';
function compareText(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
export function canonicalizeFredrickSourceImageIds(values) {
    if (!Array.isArray(values) || values.length === 0) {
        throw new TypeError('sourceImageIds must be a non-empty array.');
    }
    const result = [...values].sort(compareText);
    for (let index = 0; index < result.length; index += 1) {
        const value = result[index];
        if (!isSourceImageId(value)) {
            throw new TypeError('sourceImageIds must contain canonical source image IDs.');
        }
        if (index > 0 && result[index - 1] === value) {
            throw new TypeError('sourceImageIds must be unique.');
        }
    }
    return result;
}
function assertIdentifier(value, field) {
    if (typeof value !== 'string' || value.length === 0 || value.length > 256) {
        throw new TypeError(`${field} must be a non-empty string of at most 256 characters.`);
    }
}
function rotateRight(value, count) {
    return (value >>> count) | (value << (32 - count));
}
/** Small synchronous SHA-256 used only for tiny canonical claim payloads. */
export function sha256Utf8(value) {
    const bytes = new TextEncoder().encode(value);
    const bitLength = bytes.length * 8;
    const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
    const padded = new Uint8Array(paddedLength);
    padded.set(bytes);
    padded[bytes.length] = 0x80;
    const view = new DataView(padded.buffer);
    const high = Math.floor(bitLength / 0x1_0000_0000);
    const low = bitLength >>> 0;
    view.setUint32(paddedLength - 8, high, false);
    view.setUint32(paddedLength - 4, low, false);
    const constants = new Uint32Array([
        0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
        0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
        0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
        0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
        0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
        0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
        0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
    ]);
    const hash = new Uint32Array([
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
        0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
    ]);
    const words = new Uint32Array(64);
    for (let offset = 0; offset < padded.length; offset += 64) {
        for (let index = 0; index < 16; index += 1) {
            words[index] = view.getUint32(offset + index * 4, false);
        }
        for (let index = 16; index < 64; index += 1) {
            const w15 = words[index - 15];
            const w2 = words[index - 2];
            const s0 = rotateRight(w15, 7) ^ rotateRight(w15, 18) ^ (w15 >>> 3);
            const s1 = rotateRight(w2, 17) ^ rotateRight(w2, 19) ^ (w2 >>> 10);
            words[index] = (words[index - 16] + s0 + words[index - 7] + s1) >>> 0;
        }
        let a = hash[0];
        let b = hash[1];
        let c = hash[2];
        let d = hash[3];
        let e = hash[4];
        let f = hash[5];
        let g = hash[6];
        let h = hash[7];
        for (let index = 0; index < 64; index += 1) {
            const s1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
            const choose = (e & f) ^ (~e & g);
            const temp1 = (h + s1 + choose + constants[index] + words[index]) >>> 0;
            const s0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
            const majority = (a & b) ^ (a & c) ^ (b & c);
            const temp2 = (s0 + majority) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + temp1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temp1 + temp2) >>> 0;
        }
        hash[0] = (hash[0] + a) >>> 0;
        hash[1] = (hash[1] + b) >>> 0;
        hash[2] = (hash[2] + c) >>> 0;
        hash[3] = (hash[3] + d) >>> 0;
        hash[4] = (hash[4] + e) >>> 0;
        hash[5] = (hash[5] + f) >>> 0;
        hash[6] = (hash[6] + g) >>> 0;
        hash[7] = (hash[7] + h) >>> 0;
    }
    const hex = [...hash].map((word) => word.toString(16).padStart(8, '0')).join('');
    return `sha256:${hex}`;
}
function rootPayload(input) {
    assertIdentifier(input.repositoryId, 'repositoryId');
    return JSON.stringify({
        schemaVersion: FREDRICK_SEARCH_CLAIM_SCHEMA_VERSION,
        repositoryId: input.repositoryId,
        sourceImageIds: canonicalizeFredrickSourceImageIds(input.sourceImageIds),
    });
}
function continuationPayload(input) {
    assertIdentifier(input.repositoryId, 'repositoryId');
    assertIdentifier(input.searchId, 'searchId');
    assertIdentifier(input.parentClaimId, 'parentClaimId');
    return JSON.stringify({
        schemaVersion: FREDRICK_SEARCH_CLAIM_SCHEMA_VERSION,
        repositoryId: input.repositoryId,
        searchId: input.searchId,
        parentClaimId: input.parentClaimId,
        sourceImageIds: canonicalizeFredrickSourceImageIds(input.sourceImageIds),
    });
}
function claimId(payload) {
    return `${FREDRICK_SEARCH_CLAIM_ID_PREFIX}:${sha256Utf8(payload)}`;
}
export function createFredrickRootSearchClaimV1(input) {
    const sourceImageIds = canonicalizeFredrickSourceImageIds(input.sourceImageIds);
    const id = claimId(rootPayload({ ...input, sourceImageIds }));
    return {
        schemaVersion: FREDRICK_SEARCH_CLAIM_SCHEMA_VERSION,
        claimId: id,
        repositoryId: input.repositoryId,
        searchId: id,
        sourceImageIds,
    };
}
export function createFredrickContinuationSearchClaimV1(input) {
    const sourceImageIds = canonicalizeFredrickSourceImageIds(input.sourceImageIds);
    const id = claimId(continuationPayload({ ...input, sourceImageIds }));
    return {
        schemaVersion: FREDRICK_SEARCH_CLAIM_SCHEMA_VERSION,
        claimId: id,
        repositoryId: input.repositoryId,
        searchId: input.searchId,
        parentClaimId: input.parentClaimId,
        sourceImageIds,
    };
}
export function isFredrickSearchClaimId(value) {
    return /^claim-v1:sha256:[0-9a-f]{64}$/u.test(value);
}
export function fredrickSearchClaimIsCanonical(claim) {
    try {
        const expected = claim.parentClaimId === undefined
            ? createFredrickRootSearchClaimV1({
                repositoryId: claim.repositoryId,
                sourceImageIds: claim.sourceImageIds,
            })
            : createFredrickContinuationSearchClaimV1({
                repositoryId: claim.repositoryId,
                searchId: claim.searchId,
                parentClaimId: claim.parentClaimId,
                sourceImageIds: claim.sourceImageIds,
            });
        return JSON.stringify(expected) === JSON.stringify(claim);
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=claims.js.map